import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FiCheck, FiSave, FiPlus, FiClock, FiBell, FiCalendar } from 'react-icons/fi';

import { useJournalEntryState } from '../../../hooks/useJournalEntryState';
import { useJournalEntries } from '../../../hooks/useJournalEntries';
import { useFiscalSponsor } from '../../../hooks/useFiscalSponsor';
import { validateEntry } from '../../../utils/journalEntryUtils';
import { TRANSACTION_TEMPLATES } from '../../../constants/transactionTemplates';
import { 
  FundType,
  RestrictionType,
  AccountingCategorizationValues
} from '../../../types/accounting';
import { 
  JournalEntry,
  JournalEntryType,
  LineItem,
  RenewalInfo,
  RecurringSchedule,
  TransactionTemplate,
  AccountingCategorization as AccountingCategorizationType
} from '../../../types/journalEntry';
import { ScheduleState } from '../../../types/journalEntry';

import {
  SmartReceiptUploader,
  FundingAllocationInputs,
  AccountingCategorization,
  LineItemRow,
  TransactionTemplateSelector,
  RecurringScheduleForm,
  RenewalInfoForm,
  JournalEntryValidation
} from './journalEntry';
import { RecurringScheduleForm as RecurringScheduleFormComponent } from './journalEntry/RecurringScheduleForm';
import { RenewalReminderForm } from './journalEntry/RenewalReminderForm';
import { ScheduledPaymentForm } from './journalEntry/ScheduledPaymentForm';

const FormContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(180deg, rgba(26, 31, 46, 0.7) 0%, rgb(26, 31, 46) 100%)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)',
    opacity: 0.5
  }
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  padding: '10px 16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    color: '#0EA5E9',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    color: '#7DD3FC',
    '&:hover': {
      backgroundColor: 'rgba(14, 165, 233, 0.2)',
    }
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(34, 39, 58, 0.7)',
    backdropFilter: 'blur(8px)',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0EA5E9',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#94A3B8',
    '&.Mui-focused': {
      color: '#38BDF8',
    }
  },
  '& .MuiInputBase-input': {
    color: '#E2E8F0',
    '&::placeholder': {
      color: '#64748B',
    }
  },
  '& .MuiSelect-icon': {
    color: '#94A3B8',
  },
  '& .MuiMenu-paper': {
    backgroundColor: 'rgb(26, 31, 46)',
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(12px)',
    '& .MuiMenu-list': {
      padding: '8px',
    }
  }
}));

const SectionDivider = styled(Box)(({ theme }) => ({
  height: '1px',
  background: 'linear-gradient(90deg, rgba(14, 165, 233, 0.3) 0%, rgba(16, 185, 129, 0.3) 100%)',
  margin: `${theme.spacing(4)} 0`,
  width: '100%'
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '8px 24px',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  fontWeight: 500,
  '&.MuiButton-contained': {
    background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)',
    '&:hover': {
      background: 'linear-gradient(90deg, #0287c8 0%, #0e9b6e 100%)',
    }
  },
  '&.MuiButton-outlined': {
    borderColor: 'rgba(255, 255, 255, 0.12)',
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    }
  }
}));

const ActionButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '12px',
  backdropFilter: 'blur(8px)',
}));

const ActionToggle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  color: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    background: 'rgba(14, 165, 233, 0.1)',
    color: '#0EA5E9',
  },
  '& svg': {
    fontSize: '1.2rem',
  }
}));

interface JournalEntryFormProps {
  onSubmit: (values: JournalEntry) => void;
  onCancel?: () => void;
  initialData?: Partial<JournalEntry>;
  isLoading?: boolean;
}

// Extend JournalEntry type to include scheduleOptions
interface EnhancedJournalEntry extends JournalEntry {
  scheduleOptions?: ScheduleState;
}

export const JournalEntryForm: React.FC<JournalEntryFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isLoading = false
}) => {
  const { fiscalSponsor } = useFiscalSponsor();
  const { addEntry } = useJournalEntries();
  
  const defaultCategorizationValues: AccountingCategorizationValues = {
    accountCategory: '',
    accountSubcategory: '',
    functionType: '',
    form990Category: '',
    fiscalSponsorshipType: '',
    form990Subcategory: '',
    accountingCode: ''
  };

  const [scheduleState, setScheduleState] = useState<ScheduleState>({
    isScheduledPayment: false,
    scheduledPaymentDate: null,
    hasRenewalReminder: false,
    isRecurring: false,
    recurringSchedule: {
      frequency: 'monthly',
      startDate: new Date(),
      endDate: null
    },
    renewalInfo: {
      type: 'subscription',
      nextRenewalDate: null,
      notifyBefore: 30,
      autoRenew: false,
      renewalDate: null,
      reminderDays: [7, 14, 30],
      contractTerm: '12',
      termType: 'months',
      notes: ''
    }
  });

  const [formData, setFormData] = useState<Partial<JournalEntry>>({
    type: 'standard' as JournalEntryType,
    status: 'draft',
    template: undefined,
    date: new Date(),
    description: '',
    lineItems: [],
    fiscalSponsorId: fiscalSponsor?.id || ''
  });

  // Store templates in a ref to avoid re-renders
  const templatesRef = useRef<Record<string, TransactionTemplate>>({});

  // Initialize templates on component mount
  useEffect(() => {
    const templatesRecord: Record<string, TransactionTemplate> = {};
    Object.values(TRANSACTION_TEMPLATES).forEach(template => {
      templatesRecord[template.id] = template;
    });
    templatesRef.current = templatesRecord;
  }, []);

  const {
    lineItems,
    addLineItem,
    removeLineItem,
    updateLineItem,
    isSubmitting,
    setIsSubmitting,
    isBalanced,
    pendingAttachments,
    setPendingAttachments,
    validationResults,
    clearFormErrors
  } = useJournalEntryState({ 
    initialEntry: initialData 
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const validation = validateEntry(formData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Use EnhancedJournalEntry to allow scheduleOptions
      const enrichedFormData: EnhancedJournalEntry = {
        ...formData as JournalEntry,
        fiscalSponsorId: formData.fiscalSponsorId!,
        date: formData.date!,
        description: formData.description!,
        type: formData.type! as JournalEntryType,
        status: formData.status!,
        lineItems: lineItems.map(item => ({
          ...item,
          fundType: (item.fundType || FundType.OPERATING) as FundType,
          restrictionType: (item.restrictionType || RestrictionType.UNRESTRICTED) as RestrictionType
        })),
        scheduleOptions: scheduleState,
        recurringSchedule: scheduleState.isRecurring ? scheduleState.recurringSchedule : null,
        renewalInfo: scheduleState.hasRenewalReminder ? scheduleState.renewalInfo : null
      };

      // Pass to onSubmit as JournalEntry (scheduleOptions will be ignored by TypeScript but available at runtime)
      await onSubmit(enrichedFormData);
      onCancel?.();
    } catch (error) {
      console.error('Error submitting journal entry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle template selection with proper typing
  const handleTemplateSelect = (template: TransactionTemplate | null) => {
    if (!template) return;
    
    const templateId = template.id;
    
    // Use type assertion to access properties that might not be in the type definition
    const templateData = template as any;
    
    setFormData(prev => ({
      ...prev,
      template: templateId,
      description: template.description || prev.description,
      // Use optional chaining and fallbacks for properties that might not exist in the type
      date: templateData.date || prev.date,
      type: templateData.type || prev.type,
      status: templateData.status || prev.status,
      lineItems: template.lineItems?.map((item: any) => ({
        ...item,
        fundType: item.fundType || FundType.OPERATING,
        restrictionType: item.restrictionType || RestrictionType.UNRESTRICTED
      })) || [],
      fiscalSponsorId: templateData.fiscalSponsorId || prev.fiscalSponsorId || '',
      accountingCategorization: templateData.accountingCategorization || defaultCategorizationValues
    }));
  };

  // Get template object from ID
  const getTemplateById = (templateId: string | undefined): TransactionTemplate | null => {
    if (!templateId) return null;
    return templatesRef.current[templateId] || null;
  };

  const handleRecurringToggle = () => {
    setScheduleState((prev: ScheduleState) => ({
      ...prev,
      isRecurring: !prev.isRecurring,
      recurringSchedule: !prev.isRecurring ? {
        frequency: 'monthly',
        startDate: new Date(),
        endDate: null
      } : null
    }));
  };

  const handleRenewalReminderToggle = () => {
    setScheduleState((prev: ScheduleState) => ({
      ...prev,
      hasRenewalReminder: !prev.hasRenewalReminder,
      renewalInfo: !prev.hasRenewalReminder ? {
        type: 'subscription',
        nextRenewalDate: null,
        notifyBefore: 30,
        autoRenew: false,
        renewalDate: null,
        reminderDays: [7, 14, 30],
        contractTerm: '12',
        termType: 'months',
        notes: ''
      } : prev.renewalInfo
    }));
  };

  const handleScheduledPaymentToggle = () => {
    setScheduleState((prev: ScheduleState) => ({
      ...prev,
      isScheduledPayment: !prev.isScheduledPayment,
      scheduledPaymentDate: !prev.isScheduledPayment ? new Date() : null
    }));
  };

  const mapFrequencyToRecurringForm = (frequency: string): "monthly" | "weekly" | "quarterly" | "annually" => {
    switch (frequency) {
      case 'daily':
        return 'weekly';
      case 'yearly':
        return 'annually';
      case 'weekly':
      case 'monthly':
      case 'quarterly':
        return frequency as "monthly" | "weekly" | "quarterly";
      default:
        return 'monthly';
    }
  };

  return (
    <FormContainer>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TransactionTemplateSelector
              selectedTemplate={getTemplateById(formData.template as string)}
              onTemplateSelect={handleTemplateSelect}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              type="date"
              label="Date"
              value={formData.date?.toISOString().split('T')[0] || ''}
              onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value) })}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledTextField
              select
              fullWidth
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as JournalEntryType })}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgb(26, 31, 46)',
                      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(12px)',
                    }
                  }
                }
              }}
            >
              <StyledMenuItem value="standard">
                <Typography sx={{ color: '#7DD3FC' }}>Standard Entry</Typography>
              </StyledMenuItem>
              <StyledMenuItem value="adjustment">
                <Typography sx={{ color: '#BAE6FD' }}>Adjustment Entry</Typography>
              </StyledMenuItem>
              <StyledMenuItem value="closing">
                <Typography sx={{ color: '#93C5FD' }}>Closing Entry</Typography>
              </StyledMenuItem>
            </StyledTextField>
          </Grid>

          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <SectionDivider />
          </Grid>

          <ActionButtonGroup>
            <ActionToggle onClick={addLineItem}>
              <FiPlus />
              <Typography>Add Line Item</Typography>
            </ActionToggle>

            <ActionToggle 
              onClick={handleRecurringToggle}
              sx={{ 
                background: scheduleState.isRecurring ? 'rgba(14, 165, 233, 0.1)' : 'transparent',
                color: scheduleState.isRecurring ? '#0EA5E9' : 'inherit'
              }}
            >
              <FiClock />
              <Typography>Make Recurring</Typography>
            </ActionToggle>

            <ActionToggle 
              onClick={handleRenewalReminderToggle}
              sx={{ 
                background: scheduleState.hasRenewalReminder ? 'rgba(14, 165, 233, 0.1)' : 'transparent',
                color: scheduleState.hasRenewalReminder ? '#0EA5E9' : 'inherit'
              }}
            >
              <FiBell />
              <Typography>Set Renewal Reminder</Typography>
            </ActionToggle>

            <ActionToggle 
              onClick={handleScheduledPaymentToggle}
              sx={{ 
                background: scheduleState.isScheduledPayment ? 'rgba(14, 165, 233, 0.1)' : 'transparent',
                color: scheduleState.isScheduledPayment ? '#0EA5E9' : 'inherit'
              }}
            >
              <FiCalendar />
              <Typography>Schedule Payment</Typography>
            </ActionToggle>
          </ActionButtonGroup>

          {/* Line Items Section */}
          {lineItems.map((item, index) => (
            <LineItemRow
              key={item.id}
              item={item}
              index={index}
              enableAllocations={true}
              onUpdate={(updates) => updateLineItem(item.id, updates)}
              onDelete={() => removeLineItem(item.id)}
            />
          ))}

          {/* Conditionally render scheduling forms */}
          {scheduleState.isRecurring && (
            <RecurringScheduleForm
              schedule={{
                frequency: mapFrequencyToRecurringForm(scheduleState.recurringSchedule?.frequency || 'monthly'),
                startDate: scheduleState.recurringSchedule?.startDate || new Date(),
                endDate: scheduleState.recurringSchedule?.endDate || null
              }}
              onScheduleChange={(schedule) => setScheduleState(prev => ({
                ...prev,
                recurringSchedule: {
                  ...schedule,
                  frequency: schedule.frequency
                }
              }))}
            />
          )}

          {scheduleState.hasRenewalReminder && (
            <RenewalInfoForm
              renewalInfo={scheduleState.renewalInfo!}
              onRenewalInfoChange={(info) => setScheduleState(prev => ({
                ...prev,
                renewalInfo: info
              }))}
            />
          )}

          {scheduleState.isScheduledPayment && (
            <RecurringScheduleForm
              schedule={{
                frequency: 'monthly',
                startDate: scheduleState.scheduledPaymentDate || new Date(),
                endDate: null
              }}
              onScheduleChange={(schedule) => setScheduleState(prev => ({
                ...prev,
                scheduledPaymentDate: schedule.startDate
              }))}
              isScheduledPayment={true}
            />
          )}

          <Grid item xs={12}>
            <SectionDivider />
          </Grid>

          {/* Additional Sections */}
          {scheduleState.renewalInfo && (
            <Grid item xs={12}>
              <RenewalInfoForm
                renewalInfo={scheduleState.renewalInfo}
                onRenewalInfoChange={(info) => setScheduleState(prev => ({
                  ...prev,
                  renewalInfo: info
                }))}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FundingAllocationInputs
              allocations={formData.allocations || []}
              onAllocationsChange={(allocations) => 
                setFormData({ ...formData, allocations })}
              variant="journal"
            />
          </Grid>

          <Grid item xs={12}>
            <AccountingCategorization
              initialValues={formData.accountingCategorization ? {
                accountCategory: formData.accountingCategorization.accountCategory || '',
                accountSubcategory: formData.accountingCategorization.accountSubcategory || '',
                functionType: formData.accountingCategorization.functionType || '',
                form990Category: formData.accountingCategorization.form990Category || '',
                fiscalSponsorshipType: (formData.accountingCategorization as any)?.fiscalSponsorshipType || '',
                form990Subcategory: formData.accountingCategorization.form990Subcategory || '',
                accountingCode: formData.accountingCategorization.accountingCode || ''
              } : defaultCategorizationValues}
              onCategoryChange={(categorization) => 
                setFormData({ ...formData, accountingCategorization: categorization as AccountingCategorizationType })}
              lineItemDescription={formData.description || ''}
              selectedAccount={formData.accountCode || ''}
              amount={formData.totalAmount || 0}
            />
          </Grid>

          <Grid item xs={12}>
            <SmartReceiptUploader
              onAttachmentsChange={setPendingAttachments}
              maxFiles={12}
            />
          </Grid>

          <Grid item xs={12}>
            <JournalEntryValidation
              validationResults={{
                errors: validationResults.errors,
                warnings: validationResults.warnings,
                info: []
              }}
              formErrors={[]}
              onClearErrors={clearFormErrors}
            />
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 2,
              mt: 2 
            }}>
              <ActionButton 
                onClick={onCancel}
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="contained"
                type="submit"
                disabled={!isBalanced || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <FiSave />}
              >
                {initialData ? 'Update Entry' : 'Create Entry'}
              </ActionButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormContainer>
  );
};

export default JournalEntryForm; 